import { useEffect, useLayoutEffect, useRef } from 'react';

import { useRealtime } from './useRealtime';

/**
 * Subscribe to a realtime event for the lifetime of the component.
 *
 * The handler is stored in a ref (updated via useLayoutEffect so it is always
 * current before any effects fire) so a changing handler identity does NOT
 * churn the Socket.IO subscription — only `event` changes cause a re-subscribe.
 */
export function useRealtimeEvent<T = unknown>(event: string, handler: (payload: T) => void): void {
  const { subscribe } = useRealtime();

  // Keep handler in a ref updated in a layout effect so it's always the latest
  // version without causing re-subscriptions.
  const handlerRef = useRef(handler);
  useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const stableHandler = (...args: unknown[]) => handlerRef.current(args[0] as T);
    const unsubscribe = subscribe(event, stableHandler);
    return unsubscribe;
  }, [event, subscribe]);
}
